import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { progressUpdatesApi, verificationRunsApi } from '../../utils/api';
import { fmtDate, fmtDateTime } from './dates';

// PRD-030 WU-13 — Progress & updates, built to the ratified mockup
// (rev 2, surface s2). For the AGENCY: the ahead/behind read per Outcome,
// a "Share a progress update" action (a courtesy snapshot — it never
// changes what the client can see), and one-click result confirmation
// (F7: finalizes a Delivered result toward settlement; the client already
// sees it live). For the CLIENT: the received summaries — with the
// reminder that live status is always open on the Delivery status tab.

const STATUS_CHIP = {
  on_track: { label: 'On track', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  behind: { label: 'Behind', cls: 'text-amber-800 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  not_measurable_yet: { label: 'Not measurable yet', cls: 'text-gray-600 bg-gray-50 border-gray-200', dot: 'bg-gray-400' },
  awaiting_evidence: { label: 'Awaiting evidence', cls: 'text-gray-600 bg-gray-50 border-gray-200', dot: 'bg-gray-400' },
  checking_now: { label: 'Checking now', cls: 'text-sky-700 bg-sky-50 border-sky-200', dot: 'bg-sky-500' },
  delivered_confirmed: { label: 'Delivered', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  delivered_pending_confirmation: { label: 'Delivered — pending agency confirmation', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  not_delivered: { label: 'Not delivered', cls: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' },
  needs_human_review: { label: 'Needs human review', cls: 'text-amber-800 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
};

function Chip({ status }) {
  const c = STATUS_CHIP[status] || STATUS_CHIP.awaiting_evidence;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[12px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${c.cls}`}>
      <span className={`w-2 h-2 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

function rollupLine(rows) {
  const count = (s) => rows.filter((r) => r.delivery && r.delivery.status === s).length;
  const parts = [];
  const ahead = count('on_track') + count('delivered_confirmed') + count('delivered_pending_confirmation');
  const behind = count('behind') + count('not_delivered');
  const waiting = count('not_measurable_yet') + count('awaiting_evidence');
  const review = count('needs_human_review');
  if (ahead) parts.push(`${ahead} outcome${ahead === 1 ? '' : 's'} ahead`);
  if (behind) parts.push(`${behind} behind`);
  if (review) parts.push(`${review} need${review === 1 ? 's' : ''} a person's eyes`);
  if (waiting) parts.push(`${waiting} not yet measurable`);
  return parts.length ? `${parts.join(', ')}.` : 'Nothing to report yet.';
}

function subLine(row) {
  const d = row.delivery || {};
  const bits = [];
  if (row.activeTo) {
    bits.push(d.window_closed ? `closed ${fmtDate(row.activeTo, { year: false })}` : `due ${fmtDate(row.activeTo, { year: false })}`);
  }
  if (d.last_checked_at) bits.push(`last checked ${fmtDateTime(d.last_checked_at)}`);
  return bits.join(' · ');
}

export default function ProgressUpdates({ agreementId, rows, isAgency, clientName, onOpenStatus }) {
  const [updates, setUpdates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [composing, setComposing] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [uRes, rRes] = await Promise.all([
        progressUpdatesApi.list(agreementId),
        verificationRunsApi.list(agreementId),
      ]);
      setUpdates(uRes.data.progress_updates || []);
      setRuns(rRes.data.runs || []);
      setError(null);
    } catch (err) {
      setError("Couldn't load updates.");
    } finally {
      setLoaded(true);
    }
  }, [agreementId]);

  useEffect(() => { refresh(); }, [refresh]);

  const pendingRuns = useMemo(
    () => runs.filter((r) => r.status === 'awaiting_confirmation' || r.status === 'budget_exhausted'),
    [runs]
  );
  const lastSent = updates[0] || null;

  const send = async () => {
    setBusy(true);
    try {
      await progressUpdatesApi.send(agreementId, note.trim());
      setNote('');
      setComposing(false);
      await refresh();
    } catch (err) {
      setError("Couldn't send the update — try again.");
    } finally {
      setBusy(false);
    }
  };

  const confirmResults = async () => {
    if (pendingRuns.length === 0) return;
    setBusy(true);
    try {
      await verificationRunsApi.confirm(agreementId, pendingRuns[0].id);
      await refresh();
    } catch (err) {
      setError("Couldn't confirm the result — try again.");
    } finally {
      setBusy(false);
    }
  };

  // Never flash "No progress summaries yet" while the first fetch is in
  // flight — an empty state must mean empty, not still-loading (WU-14 U3).
  if (!loaded) {
    return <div className="card p-6 text-center text-gray-600 text-sm" data-testid="progress-loading">Loading updates…</div>;
  }
  if (error) {
    return (
      <div className="card p-6 text-center text-sm" data-testid="progress-error">
        <div className="text-gray-600 mb-3">{error}</div>
        <button type="button" className="text-[12.5px] font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg px-3.5 py-2" onClick={refresh}>
          Try again
        </button>
      </div>
    );
  }

  // ---- CLIENT: received summaries ----------------------------------------
  if (!isAgency) {
    return (
      <div>
        <p className="text-[13.5px] text-gray-600 mb-4 max-w-2xl">
          You can see live delivery status any time on the <b className="text-gray-700">Delivery status</b> tab — nothing
          is hidden. Here are the progress summaries you've been sent.
        </p>
        {updates.length === 0 ? (
          <div className="card p-6 text-center text-gray-600 text-[13px]">No progress summaries yet.</div>
        ) : (
          <div className="card overflow-hidden">
            {updates.map((u) => (
              <div key={u.id} className="border-b border-gray-100 last:border-b-0">
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <span className="flex-1">
                    <span className="text-[14px] font-semibold text-gray-900">Progress update</span>
                    <span className="block text-[11.5px] text-gray-600 mt-0.5">
                      Sent by {u.sent_by_name} · {fmtDateTime(u.created_at)}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="text-[12px] text-primary-600 font-semibold"
                    onClick={() => setOpenUpdate(openUpdate === u.id ? null : u.id)}
                  >
                    {openUpdate === u.id ? 'Close' : 'Open'}
                  </button>
                </div>
                {openUpdate === u.id && (
                  <div className="px-4 pb-4">
                    {u.note && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] text-gray-700 mb-2.5">{u.note}</div>
                    )}
                    {(u.snapshot || []).map((s) => (
                      <div key={s.outcome_id} className="flex items-center gap-3 py-1.5">
                        <span className="text-[13px] text-gray-800 flex-1">{s.name}</span>
                        {s.status && <Chip status={s.status} />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- AGENCY: cockpit + share + confirmation ------------------------------
  return (
    <div>
      <p className="text-[13.5px] text-gray-600 mb-4 max-w-2xl">
        Where this campaign stands as of <b className="text-gray-700">{fmtDate(new Date(), { year: false })}</b>. Your
        client sees the same live status any time — this is your read on where you're ahead and where you need to push,
        plus a way to send a summary.
      </p>

      {pendingRuns.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-[13px] px-4 py-3 mb-3" data-testid="confirm-banner">
          <span className="flex-1">
            <b>{pendingRuns.length === 1 ? 'A delivery result awaits' : `${pendingRuns.length} delivery results await`} your confirmation.</b>{' '}
            Your client already sees {pendingRuns.length === 1 ? 'it' : 'them'} live — confirming finalizes toward settlement.
          </span>
          <button
            type="button"
            className="text-[12.5px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3.5 py-2"
            onClick={confirmResults}
            disabled={busy}
          >
            {busy ? 'Confirming…' : pendingRuns.length === 1 ? 'Confirm result' : 'Confirm results'}
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 text-sky-800 text-[13.5px] px-4 py-3 mb-3">
        <span className="flex-1"><b>{rollupLine(rows)}</b></span>
        {!composing && (
          <button
            type="button"
            className="text-[12.5px] font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg px-3.5 py-2"
            onClick={() => setComposing(true)}
          >
            Share a progress update
          </button>
        )}
      </div>

      {composing && (
        <div className="card p-4 mb-3" data-testid="compose-update">
          <div className="text-[13px] font-semibold text-gray-900 mb-2">
            Send {clientName || 'your client'} a snapshot of today's numbers
          </div>
          <textarea
            className="input w-full text-[13px]"
            rows={3}
            placeholder="Optional note — e.g. where you're ahead, what you're pushing on next."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex justify-end gap-2.5 mt-2.5">
            <button type="button" className="text-[12.5px] font-semibold border border-gray-200 text-gray-600 rounded-lg px-3.5 py-2" onClick={() => setComposing(false)} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="text-[12.5px] font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg px-3.5 py-2" onClick={send} disabled={busy}>
              {busy ? 'Sending…' : 'Send update'}
            </button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        {rows.map((row) => (
          <div key={row.outcomeId} className="flex items-center gap-3.5 px-4 py-3.5 border-b border-gray-100 last:border-b-0">
            <span className="flex-1">
              <span className="text-[14px] font-semibold text-gray-900">{row.name}</span>
              <span className="block text-[11.5px] text-gray-600 mt-0.5 tabular-nums">{subLine(row)}</span>
            </span>
            {row.delivery && <Chip status={row.delivery.status} />}
            <button type="button" className="text-[12px] text-primary-600 font-semibold" onClick={onOpenStatus}>
              Details
            </button>
          </div>
        ))}
      </div>

      <p className="text-[13.5px] text-gray-600 mt-3 max-w-2xl">
        Sharing an update sends the client a snapshot of these numbers. It doesn't change what they can see — live status
        is always open to them.{lastSent ? <> Last update sent {fmtDate(lastSent.created_at, { year: false })}.</> : null}
      </p>
    </div>
  );
}
